import { useMemo, useState } from 'react';
import type {
  MonthlyReconciliationReport,
  RegulatoryExtractResponse,
} from '@nadoutong/contracts';
import {
  monthlyReconciliationCsvUrl,
  regulatoryExtractCsvUrl,
  useMonthlyReconciliation,
  useRegulatoryExtract,
} from '../api/reports.ts';
import { useT } from '../i18n/index.ts';

/**
 * `/reports` — compliance + finance reporting. Epic #109 P3.14 + P3.15.
 *
 * Two sections share one month picker at the top:
 *   1. Monthly reconciliation — revenue ledger ↔ paid orders drift.
 *   2. Regulatory extract — per-account monthly usage for PRC AI
 *      filing requirements.
 *
 * Both sections render summary-tile + table + CSV-download-anchor. The
 * CSV routes are raw GET anchors (download attribute) so the browser
 * attaches the session cookie and streams the file to disk without
 * caching a Blob in React Query.
 */

function fmtCny(cents: number): string {
  const yuan = cents / 100;
  return `¥${yuan.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtUsd(cents: number): string {
  const dollars = cents / 100;
  return `$${dollars.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function fmtInt(n: number): string {
  return n.toLocaleString('en-US');
}

function monthsForPicker(now: Date, count: number): string[] {
  const out: string[] = [];
  const year = now.getUTCFullYear();
  const month = now.getUTCMonth();
  for (let i = 0; i < count; i += 1) {
    const d = new Date(Date.UTC(year, month - i, 1));
    if (d.getUTCFullYear() < 2024) break;
    const y = d.getUTCFullYear();
    const m = String(d.getUTCMonth() + 1).padStart(2, '0');
    out.push(`${y}-${m}`);
  }
  return out;
}

function defaultMonth(now: Date): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function ReportsPage(): JSX.Element {
  const t = useT();
  const now = useMemo(() => new Date(), []);
  const months = useMemo(() => monthsForPicker(now, 12), [now]);
  const [month, setMonth] = useState(() => defaultMonth(now));

  return (
    <section className="flex flex-col gap-8" data-testid="reports-page">
      <header className="flex flex-col gap-3">
        <h2 className="text-3xl font-semibold tracking-tight text-on-surface">
          {t('reports.title')}
        </h2>
        <p className="text-sm text-on-surface-variant">{t('reports.subtitle')}</p>
        <div className="flex items-center gap-3" data-testid="reports-month-picker-bar">
          <label className="flex items-center gap-2 text-[11px] uppercase tracking-[0.08em] text-outline">
            {t('reports.month_label')}
            <select
              value={month}
              onChange={(e) => setMonth(e.currentTarget.value)}
              data-testid="reports-month-picker"
              className="rounded-lg border border-outline-variant bg-surface-container-lowest px-3 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
            >
              {months.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>
      </header>

      <MonthlySection month={month} />
      <RegulatorySection month={month} />
    </section>
  );
}

// ─── Monthly reconciliation (P3.14) ──────────────────────────────────

interface MonthSectionProps {
  readonly month: string;
}

function MonthlySection({ month }: MonthSectionProps): JSX.Element {
  const t = useT();
  const query = useMonthlyReconciliation(month);
  return (
    <article
      data-testid="reports-monthly-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('reports.monthly.title')}
          </h3>
          <p className="text-sm text-outline">{t('reports.monthly.body')}</p>
        </div>
        <a
          href={monthlyReconciliationCsvUrl(month)}
          download={`monthly-reconciliation-${month}.csv`}
          data-testid="reports-monthly-csv-link"
          className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
        >
          {t('reports.monthly.csv_download')}
        </a>
      </header>
      {query.isLoading ? (
        <p className="text-xs text-outline" data-testid="reports-monthly-loading">
          {t('reports.monthly.loading')}
        </p>
      ) : query.isError ? (
        <p
          className="text-xs text-error"
          role="alert"
          data-testid="reports-monthly-error"
        >
          {t('reports.monthly.error')}
        </p>
      ) : query.data !== undefined ? (
        <MonthlyReport report={query.data} />
      ) : null}
    </article>
  );
}

interface MonthlyReportProps {
  readonly report: MonthlyReconciliationReport;
}

function MonthlyReport({ report }: MonthlyReportProps): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-6">
      {report.has_drift ? (
        <div
          className="rounded-lg bg-tertiary-container p-4 text-sm text-on-tertiary-container"
          role="alert"
          data-testid="reports-monthly-drift-banner"
        >
          <strong className="font-semibold">{t('reports.monthly.drift.title')}</strong>{' '}
          {t('reports.monthly.drift.body', { amount: fmtCny(report.drift_cny_cents) })}
        </div>
      ) : null}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <SummaryTile
          testId="reports-monthly-paid"
          label={t('reports.monthly.tile.paid_cny')}
          value={fmtCny(report.paid_orders.cny_cents)}
          sub={t('reports.monthly.tile.paid_count', {
            count: fmtInt(report.paid_orders.count),
          })}
        />
        <SummaryTile
          testId="reports-monthly-topups"
          label={t('reports.monthly.tile.topups_cny_gross')}
          value={fmtCny(report.topups.cny_gross_cents)}
          sub={t('reports.monthly.tile.topups_count', {
            count: fmtInt(report.topups.count),
          })}
        />
        <SummaryTile
          testId="reports-monthly-usage-usd"
          label={t('reports.monthly.tile.usage_usd')}
          value={fmtUsd(report.usage.usd_charged_cents)}
          sub={t('reports.monthly.tile.usage_rows', {
            rows: fmtInt(report.usage.rows),
          })}
        />
        <SummaryTile
          testId="reports-monthly-l2"
          label={t('reports.monthly.tile.l2_profit')}
          value={fmtCny(report.usage.l2_profit_cny_cents)}
          sub={t('reports.monthly.tile.l1_profit', {
            amount: fmtCny(report.topups.l1_profit_cny_cents),
          })}
        />
      </div>
      <div className="overflow-x-auto">
        <table
          className="w-full text-left text-xs"
          data-testid="reports-monthly-detail-table"
        >
          <thead>
            <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
              <th scope="col" className="py-2 pr-4 font-semibold">
                {t('reports.monthly.detail.metric')}
              </th>
              <th scope="col" className="py-2 pr-4 text-right font-semibold">
                {t('reports.monthly.detail.value')}
              </th>
            </tr>
          </thead>
          <tbody>
            <DetailRow
              label={t('reports.monthly.detail.period_start')}
              value={report.period_start}
              mono
            />
            <DetailRow
              label={t('reports.monthly.detail.period_end')}
              value={report.period_end}
              mono
            />
            <DetailRow
              label={t('reports.monthly.detail.paid_cny_net')}
              value={fmtCny(report.topups.cny_net_cents)}
            />
            <DetailRow
              label={t('reports.monthly.detail.usd_credited')}
              value={fmtUsd(report.topups.usd_credited_cents)}
            />
            <DetailRow
              label={t('reports.monthly.detail.ops_adjust_rows')}
              value={fmtInt(report.ops_adjust.rows)}
            />
            <DetailRow
              label={t('reports.monthly.detail.ops_adjust_usd')}
              value={fmtUsd(report.ops_adjust.usd_adjusted_cents)}
            />
            <DetailRow
              label={t('reports.monthly.detail.drift_cny')}
              value={fmtCny(report.drift_cny_cents)}
              highlight={report.has_drift}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Regulatory extract (P3.15) ──────────────────────────────────────

function RegulatorySection({ month }: MonthSectionProps): JSX.Element {
  const t = useT();
  const query = useRegulatoryExtract(month);
  return (
    <article
      data-testid="reports-regulatory-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-col gap-2">
          <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
            {t('reports.regulatory.title')}
          </h3>
          <p className="text-sm text-outline">{t('reports.regulatory.body')}</p>
        </div>
        <a
          href={regulatoryExtractCsvUrl(month)}
          download={`regulatory-extract-${month}.csv`}
          data-testid="reports-regulatory-csv-link"
          className="inline-flex items-center justify-center rounded-lg bg-tertiary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-tertiary-container transition-all hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
        >
          {t('reports.regulatory.csv_download')}
        </a>
      </header>
      {query.isLoading ? (
        <p className="text-xs text-outline" data-testid="reports-regulatory-loading">
          {t('reports.regulatory.loading')}
        </p>
      ) : query.isError ? (
        <p
          className="text-xs text-error"
          role="alert"
          data-testid="reports-regulatory-error"
        >
          {t('reports.regulatory.error')}
        </p>
      ) : query.data !== undefined ? (
        <RegulatoryBody response={query.data} />
      ) : null}
    </article>
  );
}

interface RegulatoryBodyProps {
  readonly response: RegulatoryExtractResponse;
}

function RegulatoryBody({ response }: RegulatoryBodyProps): JSX.Element {
  const t = useT();
  return (
    <div className="flex flex-col gap-4">
      {response.truncated ? (
        <div
          className="rounded-lg bg-tertiary-container p-3 text-xs text-on-tertiary-container"
          role="status"
          data-testid="reports-regulatory-truncated-banner"
        >
          {t('reports.regulatory.truncated')}
        </div>
      ) : null}
      <p className="text-[10px] uppercase tracking-[0.08em] text-outline">
        {t('reports.regulatory.row_count', { count: fmtInt(response.rows.length) })}
      </p>
      {response.rows.length === 0 ? (
        <p className="text-xs text-outline" data-testid="reports-regulatory-empty">
          {t('reports.regulatory.empty')}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            className="w-full text-left text-xs"
            data-testid="reports-regulatory-table"
          >
            <thead>
              <tr className="border-b border-outline-variant text-[10px] uppercase tracking-[0.08em] text-outline">
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('reports.regulatory.column.account_id')}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('reports.regulatory.column.email')}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t('reports.regulatory.column.registered_at')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('reports.regulatory.column.input_tokens')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('reports.regulatory.column.output_tokens')}
                </th>
                <th scope="col" className="py-2 pr-4 text-right font-semibold">
                  {t('reports.regulatory.column.cost_usd')}
                </th>
              </tr>
            </thead>
            <tbody>
              {response.rows.map((row) => (
                <tr
                  key={row.account_id}
                  className="border-b border-outline-variant/40"
                  data-testid={`reports-regulatory-row-${row.account_id}`}
                >
                  <td className="py-2 pr-4 font-mono text-[11px] text-outline">
                    {row.account_id}
                  </td>
                  <td className="py-2 pr-4 text-on-surface">{row.email}</td>
                  <td className="py-2 pr-4 font-mono text-[11px] text-outline">
                    {row.registered_at}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
                    {fmtInt(row.input_tokens)}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
                    {fmtInt(row.output_tokens)}
                  </td>
                  <td className="py-2 pr-4 text-right font-mono text-[11px] text-on-surface">
                    {fmtUsd(row.cost_usd_cents)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Reusable bits ────────────────────────────────────────────────────

interface SummaryTileProps {
  readonly testId: string;
  readonly label: string;
  readonly value: string;
  readonly sub: string;
}

function SummaryTile({ testId, label, value, sub }: SummaryTileProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      className="flex flex-col gap-2 rounded-lg bg-surface-container-low p-4"
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-outline">
        {label}
      </span>
      <span className="font-mono text-lg text-on-surface">{value}</span>
      <span className="text-[11px] text-outline">{sub}</span>
    </div>
  );
}

interface DetailRowProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
  readonly highlight?: boolean;
}

function DetailRow({ label, value, mono = false, highlight = false }: DetailRowProps): JSX.Element {
  const valueClass = [
    'py-2 pr-4 text-right',
    mono ? 'font-mono text-[11px]' : '',
    highlight ? 'font-semibold text-on-tertiary-container' : 'text-on-surface',
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <tr className="border-b border-outline-variant/40">
      <td className="py-2 pr-4 text-outline">{label}</td>
      <td className={valueClass}>{value}</td>
    </tr>
  );
}
